import type {SiteSettingsQueryResult} from '@/sanity/schemas/types';
import {
	ABOUT_PAGE_PATH,
	ARTICLES_PAGE_PATH,
	BANK_GUIDEBOOK_PATH,
	BANK_PAGE_PATH,
	COLLECTION_GUIDEBOOK_PATH,
	COLLECTION_PAGE_PATH,
	COMPANIES_PAGE_PATH,
	COMPLAINT_GUIDE_PAGE_PATH,
	FAQ_PAGE_PATH,
	FINANCE_PAGE_PATH,
	FOR_LAWYERS_PAGE_PATH,
	INSURANCE_GUIDEBOOK_PATH,
	INSURANCE_PAGE_PATH,
	LOGIN_PAGE_PATH,
	NEMND_PAGE_PATH,
	SEARCH_PAGE_PATH,
} from '@/shared/constants';

import type {LinkableDocumentTypes} from './schema';

export const LINK_FRAGMENT = `//groq
	...,
	reference-> {
		title,
		_type,
		"slug": slug.current,
	}
`;

export type LinkFragment = Omit<
	NonNullable<
		NonNullable<
			NonNullable<SiteSettingsQueryResult>['footerLinkGroups']
		>[number]['links']
	>[number],
	'_type' | '_key'
>;

export const internalLinkToHref = (
	documentType?: LinkableDocumentTypes,
	slug?: string,
) => {
	switch (documentType) {
		case 'frontpage':
			return '/';
		case 'page':
			return slug ? `/${slug}` : '#';
		case 'article':
			return slug ? `${ARTICLES_PAGE_PATH}/${slug}` : '#';
		case 'article-frontpage':
			return ARTICLES_PAGE_PATH;
		case 'complaintGuide-frontpage':
			return COMPLAINT_GUIDE_PAGE_PATH;
		case 'bank-area':
			return slug ? `${BANK_PAGE_PATH}/${slug}` : '#';
		case 'bank-frontpage':
			return BANK_PAGE_PATH;
		case 'bank-guidebook':
			return BANK_GUIDEBOOK_PATH;
		case 'collection-area':
			return slug ? `${COLLECTION_PAGE_PATH}/${slug}` : '#';
		case 'collection-frontpage':
			return COLLECTION_PAGE_PATH;
		case 'collection-guidebook':
			return COLLECTION_GUIDEBOOK_PATH;
		case 'insurance-area':
			return slug ? `${INSURANCE_PAGE_PATH}/${slug}` : '#';
		case 'insurance-frontpage':
			return INSURANCE_PAGE_PATH;
		case 'insurance-guidebook':
			return INSURANCE_GUIDEBOOK_PATH;
		case 'faq-frontpage':
			return FAQ_PAGE_PATH;
		case 'about-frontpage':
			return ABOUT_PAGE_PATH;
		case 'about-page':
			return slug ? `${ABOUT_PAGE_PATH}/${slug}` : '#';
		case 'nemnd':
			return slug ? `${NEMND_PAGE_PATH}/${slug}` : '#';
		case 'nemnd-frontpage':
			return NEMND_PAGE_PATH;
		case 'search-frontpage':
			return SEARCH_PAGE_PATH;
		case 'finance-frontpage':
			return FINANCE_PAGE_PATH;
		case 'finance-page':
			return slug ? `${FINANCE_PAGE_PATH}/${slug}` : '#';
		case 'login-frontpage':
			return LOGIN_PAGE_PATH;
		case 'companies-frontpage':
			return COMPANIES_PAGE_PATH;
		case 'forLawyers-frontpage':
			return FOR_LAWYERS_PAGE_PATH;
		case 'forLawyers-page':
			return slug ? `${FOR_LAWYERS_PAGE_PATH}/${slug}` : '#';
		default:
			console.warn(
				'Document type not a known linkable type:',
				documentType,
			);
			return '#';
	}
};

export const linkFragmentToHref = (link: LinkFragment) => {
	if (link.type === 'internal') {
		return internalLinkToHref(
			link.reference?._type,
			link.reference?.slug ?? '',
		);
	}

	if (link.type === 'external') {
		return link.href ?? '#';
	}

	return '#';
};
